import { compareAsc, format } from "date-fns";
import { EyWorksDatePicker } from "eyworks-ui-components";
import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import { CustomButton } from "src/app/components";
import { ErrorType } from "src/shared/types/src";

type PdfFilterModalProps = {
  filters: Partial<{
    fromDate: Date;
    toDate: Date;
  }>;
  isPdfFilterModal: boolean;
  togglePdfFilterModal: () => void;
  applyFilter: (fromDate?: Date, toDate?: Date) => void;
};

const PdfFilterModal: React.FC<PdfFilterModalProps> = ({
  filters,
  isPdfFilterModal,
  togglePdfFilterModal,
  applyFilter,
}) => {
  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState<ErrorType>({});
  const [selectedFromDate, setSelectedFromDate] = useState<Date>();
  const [selectedToDate, setSelectedToDate] = useState<Date>();
  const FIELD_MAP = {
    FROM_DATE: "fromDate",
    TO_DATE: "toDate",
  };

  useEffect(() => {
    setSelectedFromDate(filters?.fromDate);
    setSelectedToDate(filters?.toDate);
  }, [filters]);

  const handleApplyFilter = () => {
    if (Object.keys(errors)?.length) {
      return setShowErrors(true);
    }
    applyFilter(selectedFromDate, selectedToDate);
  };

  const handleClearAll = () => {
    setSelectedFromDate(undefined);
    setSelectedToDate(undefined);
    setErrors({});
  };

  const handleFromDateChange = async (selectedDate: Date) => {
    setSelectedFromDate(selectedDate);
    if (selectedToDate) {
      const compareResult = compareAsc(
        new Date(selectedDate),
        new Date(selectedToDate)
      );
      if (compareResult === 1) {
        setErrors({
          ...errors,
          [FIELD_MAP.FROM_DATE]: {
            isErrored: true,
            errorMsg: "Should be before To date",
          },
        });
      } else {
        let e = JSON.parse(JSON.stringify(errors));
        delete e[FIELD_MAP.FROM_DATE];
        delete e[FIELD_MAP.TO_DATE];
        setErrors(e);
      }
    } else {
      setErrors({
        ...errors,
        [FIELD_MAP.TO_DATE]: {
          isErrored: true,
          errorMsg: "This field is required",
        },
      });
    }
  };

  const handleToDateChange = async (selectedDate: Date) => {
    setSelectedToDate(selectedDate);
    if (selectedFromDate) {
      const compareResult = compareAsc(
        new Date(selectedDate),
        new Date(selectedFromDate)
      );
      // if end date is before start date
      if (compareResult === -1) {
        setErrors({
          ...errors,
          [FIELD_MAP.TO_DATE]: {
            isErrored: true,
            errorMsg: "Should be after From date",
          },
        });
      } else {
        let e = JSON.parse(JSON.stringify(errors));
        delete e[FIELD_MAP.FROM_DATE];
        delete e[FIELD_MAP.TO_DATE];
        setErrors(e);
      }
    } else {
      setErrors({
        ...errors,
        [FIELD_MAP.FROM_DATE]: {
          isErrored: true,
          errorMsg: "This field is required",
        },
      });
    }
  };
  return (
    <Modal
      isOpen={isPdfFilterModal}
      toggle={togglePdfFilterModal}
      backdrop={true}
      scrollable={false}
      fade={false}
      className={`custom-modal-md modal-dialog-bottom modal-dialog-bottom-style`}
      modalClassName={`modal-slide-down`}
    >
      <ModalBody className="mg-t-15">
        <div className="d-flex justify-content-between align-items-center w-100 border-bottom border-gray-600 pd-b-15 mg-b-15">
          <p className="fs-16 fw-semibold text-black mb-0">Apply Filters</p>
          <CustomButton
            text="Reset"
            color="link"
            onClick={handleClearAll}
            customBtnStyle="custom-btn-link-with-underline"
          />
        </div>
        <div className="form-group mg-b-25">
          <div className="d-flex timeline-range-date-picker">
            <div className="flex-1">
              <div className="fs-14 fw-semibold text-black mg-b-13">From</div>
              <EyWorksDatePicker
                customDateValue={
                  selectedFromDate
                    ? format(selectedFromDate, "d MMM yyyy")
                    : "Select Date"
                }
                onChange={handleFromDateChange}
                selectedDate={selectedFromDate}
              />
              {showErrors &&
              errors &&
              errors[FIELD_MAP.FROM_DATE] &&
              errors[FIELD_MAP.FROM_DATE].isErrored ? (
                <span className="form-error">
                  {errors[FIELD_MAP.FROM_DATE].errorMsg}
                </span>
              ) : null}
            </div>
            <span className="date-divider">-</span>
            <div className="flex-1">
              <div className="fs-14 fw-semibold text-black mg-b-13">To</div>
              <EyWorksDatePicker
                customDateValue={
                  selectedToDate
                    ? format(selectedToDate, "d MMM yyyy")
                    : "Select Date"
                }
                onChange={handleToDateChange}
                selectedDate={selectedToDate}
              />
              {showErrors &&
              errors &&
              errors[FIELD_MAP.TO_DATE] &&
              errors[FIELD_MAP.TO_DATE].isErrored ? (
                <span className="form-error">
                  {errors[FIELD_MAP.TO_DATE].errorMsg}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <CustomButton
          text="Apply"
          color="primary"
          onClick={handleApplyFilter}
        />
      </ModalFooter>
    </Modal>
  );
};

export default PdfFilterModal;
